import type { FiltrosDaLista as Criterios } from '../../../api/filtrosDeGmud'
import { cn } from '../../../lib/cn'
import { TRIAGENS, triagemAtiva } from './triagem'

interface Props {
  filtros: Criterios
  alterar: (mudanca: Partial<Criterios>) => void
}

/**
 * Os atalhos de triagem, em fileira, acima da lista.
 *
 * Existem porque três perguntas respondem por quase todo acesso à tela — o que está esperando o
 * comitê, o que está em curso e o que é emergencial — e abrir um painel, marcar duas caixas e
 * clicar em "Aplicar" para cada uma delas é o tipo de trabalho que faz o usuário parar de
 * filtrar e passar a rolar a lista inteira.
 *
 * Cada atalho escreve na URL pelos mesmos filtros do painel, então continuam valendo as três
 * promessas de `filtros.ts`: o link é compartilhável, o "voltar" desfaz, e recarregar não zera.
 *
 * São botões com `aria-pressed`, e não abas nem grupo de rádio: aba anuncia que troca de painel
 * (aqui o conteúdo é o mesmo, recortado) e rádio exige navegação por setas, que quem chega pelo
 * Tab não espera numa fileira de filtros. `aria-pressed` diz o que a coisa é — um atalho ligado
 * ou desligado — e é o que o leitor de tela anuncia junto do rótulo.
 *
 * @param props critérios correntes e a função que os altera
 * @returns a fileira de atalhos
 */
export function ChipsDeTriagem({ filtros, alterar }: Props) {
  const ativa = triagemAtiva(filtros)

  return (
    // A rolagem lateral é da fileira, não da página: em tela estreita os atalhos não cabem, e
    // quebrá-los em três linhas empurraria a lista para baixo da dobra. A barra de rolagem fica
    // à vista de propósito — escondida, ninguém descobre que há atalho fora da tela.
    <div role="group" aria-label="Atalhos para filtrar a lista" className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
      {TRIAGENS.map((triagem) => {
        const selecionada = triagem.id === ativa
        return (
          <button
            key={triagem.id}
            type="button"
            title={triagem.ajuda}
            aria-pressed={selecionada}
            onClick={() => alterar(triagem.recorte)}
            className={cn(
              'whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
              'focus-visible:outline-niteroi-600',
              selecionada
                ? 'bg-niteroi-700 text-white shadow-sm hover:bg-niteroi-800'
                : 'bg-white text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-niteroi-50 hover:text-niteroi-800',
            )}
          >
            {triagem.rotulo}
          </button>
        )
      })}
    </div>
  )
}
